import { all, call, put, select, takeEvery } from "redux-saga/effects";
import queryString from "query-string";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addCarePlanTaskFailure,
  addCarePlanTaskSuccess,
  deleteCarePlanTaskFailure,
  deleteCarePlanTaskSuccess,
  editCarePlanTaskFailure,
  editCarePlanTaskSuccess,
  fetchCarePlanTasksFailure,
  fetchCarePlanTasksSuccess,
  fetchCarePlanTaskTemplatesFailure,
  fetchCarePlanTaskTemplatesSuccess,
  markCarePlanTaskStatusFailure,
  markCarePlanTaskStatusSuccess,
} from "./actions";
import {
  ADD_CARE_PLAN_TASK,
  DELETE_CARE_PLAN_TASK,
  EDIT_CARE_PLAN_TASK,
  FETCH_CARE_PLAN_TASKS,
  FETCH_CARE_PLAN_TASK_TEMPLATES,
  MARK_CARE_PLAN_TASK_STATUS,
} from "./constants";
import {
  buildCarePlanTaskEditPayload,
  buildCarePlanTaskPayload,
} from "./schema";

const API_BASE = "/api/rd-care-plan-task";
const TEMPLATE_API_BASE = "/api/rd-task-template";

function* fetchCarePlanTasksSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? queryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `${API_BASE}?${query}`);
    yield put(fetchCarePlanTasksSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchCarePlanTasksFailure());
    yield put(errorAlert(e?.error || "Error fetching care plan tasks"));
  }
}

function* addCarePlanTaskSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `${API_BASE}/add`,
      buildCarePlanTaskPayload(payload),
    );
    yield put(addCarePlanTaskSuccess(data));
    yield put(successAlert("Care plan task created successfully"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(addCarePlanTaskFailure());
    yield put(errorAlert(e?.error || "Error creating care plan task"));
  }
}

function* editCarePlanTaskSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, data: formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `${API_BASE}/edit/${id}`,
      buildCarePlanTaskEditPayload(formData),
    );
    yield put(editCarePlanTaskSuccess(data));
    yield put(successAlert("Care plan task updated successfully"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(editCarePlanTaskFailure());
    yield put(errorAlert(e?.error || "Error updating care plan task"));
  }
}

function* deleteCarePlanTaskSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).delete, `${API_BASE}/${payload}`);
    yield put(deleteCarePlanTaskSuccess(data));
    yield put(successAlert("Care plan task deleted successfully"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(deleteCarePlanTaskFailure());
    yield put(errorAlert(e?.error || "Error deleting care plan task"));
  }
}

function* markCarePlanTaskStatusSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { id, data } = payload;
  try {
    const { data: res } = yield call(
      api(token).post,
      `${API_BASE}/mark-done/${id}`,
      data || {},
    );
    yield put(markCarePlanTaskStatusSuccess(res));
    yield put(successAlert("Task status updated"));
    typeof onSuccess === "function" && onSuccess(res);
  } catch (e) {
    yield put(markCarePlanTaskStatusFailure());
    yield put(errorAlert(e?.error || "Error updating task status"));
  }
}

function* fetchCarePlanTaskTemplatesSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? queryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `${TEMPLATE_API_BASE}/catalogue?${query}`,
    );
    yield put(fetchCarePlanTaskTemplatesSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchCarePlanTaskTemplatesFailure());
    yield put(errorAlert(e?.error || "Error fetching task templates"));
  }
}

export default function* CarePlanTaskSaga() {
  yield all([
    takeEvery(FETCH_CARE_PLAN_TASKS, fetchCarePlanTasksSaga),
    takeEvery(ADD_CARE_PLAN_TASK, addCarePlanTaskSaga),
    takeEvery(EDIT_CARE_PLAN_TASK, editCarePlanTaskSaga),
    takeEvery(DELETE_CARE_PLAN_TASK, deleteCarePlanTaskSaga),
    takeEvery(FETCH_CARE_PLAN_TASK_TEMPLATES, fetchCarePlanTaskTemplatesSaga),
    takeEvery(MARK_CARE_PLAN_TASK_STATUS, markCarePlanTaskStatusSaga),
  ]);
}
